import { useEffect, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  Box,
  Chip,
  TablePagination,
  Typography,
  Stack,
  Button,
} from "@mui/material";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";
import { NoData } from "src/shared/components/tables/no-data";
import TableActions from "./table-actions";
import TableHeadText from "./table-head-text";
import {
  useDownloadOCList,
  useGetOCTransactionsList,
} from "src/api/operation-cost/transactions";
import { useTranslation } from "react-i18next";
import { downloadAsXLS, formatNumber, oneBranchHasPermission } from "src/utils";
import AddIcon from "@mui/icons-material/Add";
import FilterIcon from "src/assets/svg-icons/aggregators/filter.svg";
import AddEditTransactionPopup from "../add-edit-transaction-popup";
import DownloadIcon from "src/assets/svg-icons/download.svg";
import FilterDrawer from "../filters";
import DateRangeInput from "src/shared/components/date-range";
import moment from "moment";
import { LoadingButton } from "@mui/lab";
import { OCPermissions } from "src/constants/permissions";
import { useSearchParams } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import Link from "@mui/material/Link";

const TransactionListTable = ({
  isAssetsOnly,
  isExpensesOnly,
}: {
  isAssetsOnly?: boolean;
  isExpensesOnly?: boolean;
}) => {
  const { t } = useTranslation("operationCost");
  const { t: generalT } = useTranslation("general");
  const [searchParams] = useSearchParams();
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const [open, setOpen] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [refNum, setRefNum] = useState(searchParams.get("reference") || "");
  const [filterStatus, setFilterStatus] = useState("all");
  const [paymentId, setPaymentId] = useState("");
  const [accountId, setAccountId] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [branchId, setBranchId] = useState("");
  const [type, setType] = useState("all");
  const [totalWithTax, setTotalWithTax] = useState("");
  const [dateRange, setDateRange] = useState<{
    startDate: string;
    endDate: string;
  }>({
    startDate: "",
    endDate: "",
  });

  //  permissions
  const addExpenseBillsPermission = oneBranchHasPermission(OCPermissions.addBill);

  const HeadText = [
    generalT("reference"),
    generalT("branch"),
    generalT("vendor"),
    ...(isAssetsOnly ? [generalT("category")] : []),
    generalT("accountName"),
    t("type"),
    generalT(isExpensesOnly ? "total" : "subTotal"),
    ...(!isExpensesOnly
      ? [
          generalT("tax"),
          generalT("totalWithTax"),
          t("paymentMethod"),
          t("paymentDate"),
          t("billDate"),
        ]
      : []),
    ...(isExpensesOnly || isAssetsOnly ? [generalT("receivedDate")] : []),
    generalT("createdBy"),
    generalT("createdAt"),
    t("status"),
    generalT("actions"),
  ];

  const filters = {
    ...(!!refNum && { reference_number: refNum }),
    ...(!!totalWithTax && { total: totalWithTax }),
    ...(!!type && type !== "all" && { type: type }),
    ...(!!filterStatus && filterStatus !== "all" && { status: filterStatus }),
    ...(!!paymentId && { payment_method_id: +paymentId }),
    ...(!!accountId && { account_id: accountId }),
    ...(!!vendorId && { vendor_id: vendorId }),
    ...(!!branchId && { branch_id: branchId }),
    due_start_date: dateRange.startDate || undefined,
    due_end_date: dateRange.endDate || undefined,
    ...(isAssetsOnly && { is_asset: 1 }),
    ...(isExpensesOnly && { is_expense: 1 }),
  };

  // APIS
  const { data, refetch, isFetching } = useGetOCTransactionsList({
    page: pageNumber + 1,
    ...filters,
  });
  const {
    data: downloadedData,
    refetch: refetchDownload,
    isFetching: isFetchingDownload,
    status,
  } = useDownloadOCList({
    ...filters,
  });

  const emptyDataArr = data?.data.length === 0;

  const handleChangePage = (event: unknown, newPage: number) => {
    setPageNumber(newPage);
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleOpenFilter = () => setOpenFilter(true);
  const handleCloseFilter = () => setOpenFilter(false);

  const handleDownload = () => {
    refetchDownload();
  };

  const getStatusChipConfig = (status: string) => {
    switch (status) {
      case "Deprecation":
        return { label: t("deprecation"), color: "info" as const };
      case "Paid":
        return { label: t("paid"), color: "success" as const };
      case "Partly Paid":
        return { label: t("partiallyPaid"), color: "warning" as const };
      case "UnPaid":
        return { label: t("unpaid"), color: "error" as const };
      case "Reminder":
        return { label: t("reminder"), color: "warning" as const };
      case "Expenses":
        return { label: t("expenses"), color: "warning" as const };
      case "OverPaid":
        return { label: t("overpaid"), color: "error" as const };
      default:
        return { label: status, color: "default" as const };
    }
  };

  useEffect(() => {
    setPageNumber(0);
  }, [
    refNum,
    totalWithTax,
    filterStatus,
    type,
    paymentId,
    accountId,
    branchId,
    dateRange,
  ]);

  useEffect(() => {
    if (data) {
      setTotal(data.meta?.total as number);
    }
  }, [data]);

  useEffect(() => {
    if (status === "success" && !!downloadedData) {
      const utf8CSVData = `\uFEFF${downloadedData}`;
      downloadAsXLS(utf8CSVData, "operation-cost");
    }
  }, [isFetchingDownload]);

  return (
    <Box overflow={"auto"} maxWidth={{ xs: "100%", md: `calc(100vw - 316px)` }}>
      <Stack
        direction={"row"}
        flexWrap={"wrap"}
        justifyContent={"space-between"}
        alignItems={"center"}
        mb={2}
      >
        <Typography
          fontWeight={600}
          lineHeight={"19.2px"}
          color={"#475467"}
          textTransform={"uppercase"}
        >
          {t(
            isAssetsOnly
              ? "fixedAssetsBills"
              : isExpensesOnly
                ? "expensesList"
                : "expensesBills",
          )}
        </Typography>
        {/* <Header refetch={refetch} /> */}
        <Stack direction={"row"} gap={1}>
          <DateRangeInput
            startDate={dateRange.startDate ? moment(dateRange.startDate) : null} // moment.Moment | null
            startDateId={`your_unique_start_date_id`}
            endDate={dateRange.endDate ? moment(dateRange.endDate) : null} // moment.Moment | null
            endDateId={`your_unique_end_date_id`}
            onDatesChange={(arg: {
              startDate: moment.Moment | null;
              endDate: moment.Moment | null;
            }) => {
              setDateRange({
                startDate: arg.startDate?.format("yyyy-MM-DD") as string,
                endDate: arg.endDate?.format("yyyy-MM-DD") as string,
              });
            }}
            showClearDates
          />
          <Button
            color="tertiary"
            variant="outlined"
            size="small"
            endIcon={<img src={FilterIcon} height={"16px"} alt="" />}
            sx={{ height: "40px" }}
            onClick={handleOpenFilter}
          >
            {generalT("filter")}
          </Button>
          <Box sx={{ minWidth: 114 }}>
            <LoadingButton
              color="tertiary"
              variant="outlined"
              endIcon={<img src={DownloadIcon} alt="" />}
              loading={isFetchingDownload}
              onClick={handleDownload}
            >
              {generalT("download")}
            </LoadingButton>
          </Box>
          {addExpenseBillsPermission && !isExpensesOnly && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              size="small"
              sx={{ height: "40px" }}
              onClick={handleOpen}
            >
              {t(isAssetsOnly ? "addFixedAssetBill" : "addNewExpense")}
            </Button>
          )}
        </Stack>
      </Stack>
      <TableContainer component={Paper} variant="outlined">
        <Table aria-label="simple table">
          <TableHead>
            <TableRow>
              {HeadText.map((item) => {
                return (
                  <TableCell key={item} sx={{ bgcolor: "#F9FAFB" }}>
                    <TableHeadText text={item} />
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          {isFetching ? (
            <TableLoadingSkeleton
              rowsLength={8}
              cellsLength={isAssetsOnly ? 17 : isExpensesOnly ? 11 : 16}
            />
          ) : (
            <TableBody>
              {data?.data.map((row: any) => {
                const isDeprecationInvoice = row.status === "Deprecation";
                const { label, color } = getStatusChipConfig(row.status);
                return (
                  <TableRow
                    key={row.id}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "#F9FAFB",
                      },
                    }}
                  >
                    <TableCell align="center">
                      <Link
                        component={RouterLink}
                        to={
                          isAssetsOnly
                            ? `/assets/cost-list/${row.id}`
                            : `/operation/transaction/${row.id}`
                        }
                        underline="always"
                        sx={{ fontSize: "inherit" }}
                      >
                        {row.reference}
                      </Link>
                    </TableCell>
                    <TableCell align="center">{row.branch_name}</TableCell>
                    <TableCell align="center">{row.vendor_name}</TableCell>
                    {isAssetsOnly ? (
                      <TableCell align="center">{row.category_name}</TableCell>
                    ) : null}
                    <TableCell align="center">{row.account_name}</TableCell>
                    <TableCell align="center">
                      {row.type === "variable"
                        ? t("variable")
                        : row.type === "asset"
                          ? t("asset")
                          : t("recurringExpenses")}
                    </TableCell>
                    <TableCell align="center">
                      {formatNumber(row.amount)}
                    </TableCell>
                    {!isExpensesOnly ? (
                      <>
                        <TableCell align="center">
                          {formatNumber(row.tax)}
                        </TableCell>
                        <TableCell align="center">
                          {formatNumber(row.total)}
                        </TableCell>
                        <TableCell align="center">
                          {row.payment_methods}
                        </TableCell>
                        <TableCell align="center">
                          {row.payment_dates}
                        </TableCell>
                        <TableCell align="center">
                          {isAssetsOnly ? (
                            row.bill_date
                          ) : row.bill_date_label === "waiting" ? (
                            <Chip
                              label="Waiting"
                              color="warning"
                              variant="outlined"
                              sx={{ height: "22px" }}
                            />
                          ) : (
                            row.bill_date_label
                          )}
                        </TableCell>
                      </>
                    ) : null}
                    {(isExpensesOnly || isAssetsOnly) && (
                      <TableCell align="center">
                        {isExpensesOnly ? row.bill_date : row.due_date}
                      </TableCell>
                    )}
                    <TableCell align="center">
                      {row.created_by} {row.added_by === "SYS" ? "(SYS)" : ""}
                    </TableCell>
                    <TableCell align="center" dir="ltr">
                      {row.created_at}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={row.status_label}
                        color={color}
                        variant="outlined"
                        sx={{ height: "22px" }}
                      />
                    </TableCell>
                    <TableCell>
                      <TableActions
                        row={row}
                        refetch={refetch}
                        isDeprecationInvoice={isDeprecationInvoice}
                        isExpensesOnly={isExpensesOnly}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          )}
        </Table>
        {emptyDataArr && <NoData />}
      </TableContainer>
      <TablePagination
        sx={{ mt: 2 }}
        rowsPerPageOptions={[20]}
        component="div"
        count={total}
        rowsPerPage={20}
        page={pageNumber}
        onPageChange={handleChangePage}
        showFirstButton
        showLastButton
      />
      <FilterDrawer
        open={openFilter}
        refNum={refNum}
        setRefNum={setRefNum}
        status={filterStatus}
        setStatus={setFilterStatus}
        paymentId={paymentId}
        setPaymentId={setPaymentId}
        accountId={accountId}
        vendorId={vendorId}
        setAccountId={setAccountId}
        setVendorId={setVendorId}
        handleClose={handleCloseFilter}
        type={type}
        setType={setType}
        branchId={branchId}
        setBranchId={setBranchId}
        totalWithTax={totalWithTax}
        setTotalWithTax={setTotalWithTax}
        isAssetsOnly={isAssetsOnly}
      />
      {open && (
        <AddEditTransactionPopup
          open={open}
          handleClose={handleClose}
          refetch={refetch}
          isAssetsOnly={isAssetsOnly}
        />
      )}
    </Box>
  );
};
export default TransactionListTable;
